import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Heart, Search, Tags, Trash2 } from "lucide-react";
import AppliedStatusIcon from "../components/AppliedStatusIcon";
import profilePlaceholder from "../assets/profile-placeholder.svg";
import ApplicantShell from "../components/ApplicantShell";
import ApplicantHomeJobPreviewCard from "../components/ApplicantHomeJobPreviewCard";
import LatestJobsPreviewPills from "../components/LatestJobsPreviewPills";
import {
  deleteApplicantSavedSearch,
  getApplicantJobSearchLastCriteria,
  getApplicantJobSearchLatestPreview,
  getApplicantAppliedJobPosts,
  getApplicantSavedJobPosts,
  isApiConfigured,
  listApplicantSavedSearches,
} from "../apiClient";
import { COUNTRIES } from "../data/countriesList";
import { applicantLastJobSearchBodyToSearchUiState } from "../jobSearch/lastJobSearchUiState";
import { buildJobSearchParamsFromState } from "../jobSearch/jobSearchUrlParams";
import { jobPublicHref } from "../lib/jobPublicPath";

const SAVED_SEARCHES_COLLAPSED_COUNT = 3;
const LATEST_JOBS_HOME_PREVIEW_MAX = 4;
const SAVED_JOBS_HOME_PREVIEW_MAX = 8;
const APPLIED_JOBS_HOME_PREVIEW_MAX = 3;

function formatAppliedOnFromIso(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatSavedSearchCriteria(criteria) {
  if (!criteria || typeof criteria !== "object") return null;
  const ISO_LABEL = new Map(COUNTRIES.map((c) => [c.value, c.label]));
  const countries = Array.isArray(criteria.criteriaCountryCodes)
    ? criteria.criteriaCountryCodes.filter(Boolean)
    : [];
  const keywords = String(criteria.criteriaTitles || "").trim();
  const workMode = String(criteria.criteriaWorkMode || "").trim();
  const currency = String(criteria.criteriaSalaryCurrency || "").trim();
  const min = String(criteria.salaryMin || "").trim();
  const max = String(criteria.salaryMax || "").trim();

  const parts = [];
  if (countries.length) {
    const longNames = countries.map((iso) => ISO_LABEL.get(iso) || iso).join(", ");
    parts.push(
      <span key="countries">
        <strong>Countries:</strong> {longNames}
      </span>
    );
  }
  if (keywords) {
    parts.push(
      <span key="keywords">
        <strong>Keywords:</strong> {keywords}
      </span>
    );
  }
  if (workMode) {
    parts.push(
      <span key="workMode">
        <strong>Work mode:</strong> {workMode}
      </span>
    );
  }
  if (currency || min || max) {
    const band = [min && `min ${min}`, max && `max ${max}`].filter(Boolean).join(", ");
    parts.push(
      <span key="salary">
        <strong>Salary:</strong> {currency || "—"}
        {band ? ` (${band})` : ""}
      </span>
    );
  }
  if (!parts.length) return null;
  return parts.reduce((acc, node, idx) => {
    if (idx === 0) return [node];
    acc.push(" | ", node);
    return acc;
  }, []);
}

function criteriaToSearchUiState(criteria) {
  if (!criteria || typeof criteria !== "object") return {};
  return {
    criteriaCountryCodes: criteria.criteriaCountryCodes,
    criteriaWorkMode: criteria.criteriaWorkMode ?? criteria.workMode ?? "",
    criteriaSalaryCurrency: criteria.criteriaSalaryCurrency,
    criteriaTitles: criteria.criteriaTitles,
    salaryMin: criteria.salaryMin,
    salaryMax: criteria.salaryMax,
    showNotForMe: criteria.showNotForMe,
    showApplied: criteria.showApplied,
    showSaved: criteria.showSaved,
  };
}

function savedSearchNavState(search) {
  return {
    searchUiState: {
      hasSearched: false,
      executeSearchOnMount: true,
      ...criteriaToSearchUiState(search.criteria),
    },
  };
}

function greetingNameFromUser(user) {
  if (!user) return "";
  const fromProfile = String(user.name || user.fullName || "").trim();
  if (fromProfile) return fromProfile;
  const email = String(user.email || "").trim();
  if (email.includes("@")) return email.split("@")[0];
  return "";
}

function ApplicantHomePage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const welcomeName = greetingNameFromUser(currentUser) || "there";
  const profilePhotoSrc = currentUser?.photoDataUrl || profilePlaceholder;
  const [apiSavedSearches, setApiSavedSearches] = useState(null);
  const [deleteSavedSearchBusyId, setDeleteSavedSearchBusyId] = useState(null);
  const [savedSearchesExpanded, setSavedSearchesExpanded] = useState(false);
  const [latestPreview, setLatestPreview] = useState(null);
  const [latestPreviewLoading, setLatestPreviewLoading] = useState(false);
  const [savedJobsPreview, setSavedJobsPreview] = useState(null);
  const [savedJobsPreviewLoading, setSavedJobsPreviewLoading] = useState(false);
  const [appliedJobsPreview, setAppliedJobsPreview] = useState(null);
  const [appliedJobsPreviewLoading, setAppliedJobsPreviewLoading] = useState(false);

  useEffect(() => {
    const t = String(authToken || "").trim();
    if (!currentUser || currentUser.role !== "applicant" || !isApiConfigured() || !t) {
      setApiSavedSearches(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await listApplicantSavedSearches(t);
        if (!cancelled) setApiSavedSearches(rows);
      } catch {
        if (!cancelled) setApiSavedSearches(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, currentUser]);

  useEffect(() => {
    const t = String(authToken || "").trim();
    if (!currentUser || currentUser.role !== "applicant" || !isApiConfigured() || !t) {
      setLatestPreview(null);
      setLatestPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setLatestPreviewLoading(true);
    (async () => {
      try {
        const data = await getApplicantJobSearchLatestPreview(t);
        if (!cancelled) setLatestPreview(data);
      } catch {
        if (!cancelled) setLatestPreview(null);
      } finally {
        if (!cancelled) setLatestPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, currentUser]);

  useEffect(() => {
    const t = String(authToken || "").trim();
    if (!currentUser || currentUser.role !== "applicant" || !isApiConfigured() || !t) {
      setSavedJobsPreview(null);
      setSavedJobsPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setSavedJobsPreviewLoading(true);
    (async () => {
      try {
        const data = await getApplicantSavedJobPosts(t, { limit: SAVED_JOBS_HOME_PREVIEW_MAX });
        if (!cancelled) setSavedJobsPreview(data);
      } catch {
        if (!cancelled) setSavedJobsPreview(null);
      } finally {
        if (!cancelled) setSavedJobsPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, currentUser]);

  useEffect(() => {
    const t = String(authToken || "").trim();
    if (!currentUser || currentUser.role !== "applicant" || !isApiConfigured() || !t) {
      setAppliedJobsPreview(null);
      setAppliedJobsPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setAppliedJobsPreviewLoading(true);
    (async () => {
      try {
        const data = await getApplicantAppliedJobPosts(t, { limit: APPLIED_JOBS_HOME_PREVIEW_MAX });
        if (!cancelled) setAppliedJobsPreview(data);
      } catch {
        if (!cancelled) setAppliedJobsPreview(null);
      } finally {
        if (!cancelled) setAppliedJobsPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, currentUser]);

  const effectiveSavedSearches = useMemo(() => {
    if (Array.isArray(apiSavedSearches)) {
      return apiSavedSearches.map((row) => ({
        id: row.id,
        title: row.name,
        when: row.createdAt ? new Date(row.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "",
        criteria: row.criteria || {},
      }));
    }
    return [];
  }, [apiSavedSearches]);

  const visibleSavedSearches = useMemo(() => {
    if (savedSearchesExpanded || effectiveSavedSearches.length <= SAVED_SEARCHES_COLLAPSED_COUNT) {
      return effectiveSavedSearches;
    }
    return effectiveSavedSearches.slice(0, SAVED_SEARCHES_COLLAPSED_COUNT);
  }, [effectiveSavedSearches, savedSearchesExpanded]);

  useEffect(() => {
    if (effectiveSavedSearches.length <= SAVED_SEARCHES_COLLAPSED_COUNT) {
      setSavedSearchesExpanded(false);
    }
  }, [effectiveSavedSearches.length]);

  const onDeleteSavedSearch = async (savedSearchId) => {
    const t = String(authToken || "").trim();
    if (!t) return;
    const id = String(savedSearchId || "").trim();
    if (!id) return;
    if (deleteSavedSearchBusyId) return;
    setDeleteSavedSearchBusyId(id);
    try {
      await deleteApplicantSavedSearch(id, t);
      setApiSavedSearches((prev) => (Array.isArray(prev) ? prev.filter((row) => String(row.id) !== id) : prev));
    } catch {
      // silent for now; avoids blocking card clicks with error UI
    } finally {
      setDeleteSavedSearchBusyId(null);
    }
  };

  const goToJobSearchWithLastCriteria = useCallback(async () => {
    const t = String(authToken || "").trim();
    if (!currentUser || currentUser.role !== "applicant" || !isApiConfigured() || !t) {
      navigate("/job-search");
      return;
    }
    try {
      const { hasLastSearch, criteria } = await getApplicantJobSearchLastCriteria(t);
      const ui = hasLastSearch ? applicantLastJobSearchBodyToSearchUiState(criteria) : null;
      if (ui) {
        const { executeSearchOnMount: _omitExec, ...rest } = ui;
        const p = buildJobSearchParamsFromState({
          criteriaCountryCodes: rest.criteriaCountryCodes || [],
          criteriaTitles: rest.criteriaTitles || "",
          criteriaWorkMode: rest.criteriaWorkMode || "",
          criteriaSalaryCurrency: rest.criteriaSalaryCurrency || "GBP",
          salaryMin: rest.salaryMin ?? "",
          salaryMax: rest.salaryMax ?? "",
          showNotForMe: rest.showNotForMe,
          showApplied: rest.showApplied,
          showSaved: rest.showSaved !== undefined ? rest.showSaved : true,
          resultsView: rest.resultsView === "tab" ? "tab" : "list",
        });
        const s = p.toString();
        navigate({ pathname: "/job-search", search: s ? `?${s}` : "" });
      } else {
        navigate("/job-search");
      }
    } catch {
      navigate("/job-search");
    }
  }, [authToken, currentUser, navigate]);

  return (
    <ApplicantShell currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken}>
      <main className="nd1-shell nd1-applicant-main">
        <section className="nd1-applicant-welcome" aria-labelledby="applicant-welcome-title">
          <div className="nd1-applicant-welcome__copy">
            <p className="nd1-applicant-welcome__eyebrow">Good to see you again,</p>
            <h1 id="applicant-welcome-title" className="nd1-applicant-welcome__title">
              {welcomeName}
            </h1>
            <p className="nd1-applicant-welcome__subtitle">Let&apos;s find your next great opportunity.</p>
          </div>
          <div className="nd1-applicant-welcome__profile">
            <Link
              to="/applicant-profile"
              className="nd1-applicant-welcome__photo-link"
              aria-label="Open your profile"
            >
              <img
                src={profilePhotoSrc}
                alt=""
                className={`nd1-applicant-welcome__photo${
                  currentUser?.photoDataUrl ? "" : " nd1-applicant-welcome__photo--placeholder"
                }`}
              />
            </Link>
          </div>
        </section>

        <section className="nd1-applicant-section" aria-labelledby="applicant-saved-searches-title">
          <div className="nd1-applicant-section-card">
            <div className="nd1-applicant-section-head">
              <div className="nd1-applicant-section-head__title-wrap">
                <span className="nd1-applicant-section-head__icon-wrap" aria-hidden="true">
                  <Search size={20} strokeWidth={1.75} />
                </span>
                <h2 id="applicant-saved-searches-title" className="nd1-applicant-section-head__title">
                  Saved job searches
                </h2>
              </div>
              <button type="button" className="nd1-btn nd1-btn--primary" onClick={() => navigate("/job-search")}>
                New Job Search
              </button>
            </div>
            {effectiveSavedSearches.length === 0 ? (
              <p className="nd1-applicant-meta">You have no saved searches yet.</p>
            ) : (
              <ul className="nd1-applicant-saved-search-list">
                {visibleSavedSearches.map((search, idx) => (
                  <li key={search.id} className="nd1-applicant-saved-search-item">
                    <span className="nd1-applicant-saved-search-item__index" aria-hidden="true">
                      {idx + 1}
                    </span>
                    <Link
                      to="/job-search"
                      state={savedSearchNavState(search)}
                      className="nd1-applicant-saved-search-item__main"
                      aria-label={`Run saved search ${search.title}`}
                    >
                      <span className="nd1-applicant-saved-search-item__title">{search.title}</span>
                      {search.criteria && typeof search.criteria === "object" ? (
                        <p className="nd1-applicant-meta nd1-applicant-meta--criteria">
                          {formatSavedSearchCriteria(search.criteria)}
                        </p>
                      ) : null}
                      <p className="nd1-applicant-saved-search-item__date">{search.when || "Saved"}</p>
                    </Link>
                    <div className="nd1-applicant-saved-search-item__actions">
                      <button
                        type="button"
                        className="nd1-applicant-saved-search-item__action nd1-applicant-saved-search-item__action--delete"
                        aria-label={`Delete saved search ${search.title}`}
                        disabled={deleteSavedSearchBusyId === String(search.id)}
                        onClick={() => onDeleteSavedSearch(search.id)}
                      >
                        <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {effectiveSavedSearches.length > SAVED_SEARCHES_COLLAPSED_COUNT ? (
              <div className="nd1-applicant-section-foot">
                <button
                  type="button"
                  className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm"
                  onClick={() => setSavedSearchesExpanded((prev) => !prev)}
                  aria-expanded={savedSearchesExpanded}
                >
                  {savedSearchesExpanded ? "See less" : "See all"}
                </button>
              </div>
            ) : null}
          </div>
        </section>

        <section className="nd1-applicant-split" aria-label="Job previews">
          <div className="nd1-applicant-split__column">
            <div className="nd1-applicant-section-card">
              <div className="nd1-applicant-section-head">
                <div className="nd1-applicant-section-head__title-wrap">
                  <span className="nd1-applicant-section-head__icon-wrap" aria-hidden="true">
                    <Tags size={20} strokeWidth={1.75} />
                  </span>
                  <h2 className="nd1-applicant-section-head__title">Latest jobs for you</h2>
                </div>
                <button
                  type="button"
                  className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm"
                  onClick={() => void goToJobSearchWithLastCriteria()}
                >
                  See all
                </button>
              </div>
              {latestPreviewLoading ? (
                <p className="nd1-applicant-meta" aria-live="polite">
                  Loading suggestions…
                </p>
              ) : !latestPreview?.hasLastSearch ? (
                <p className="nd1-applicant-meta">
                  Run a job search once and we&apos;ll show up to four live roles here that match your last criteria.{" "}
                  <Link to="/job-search">Open job search</Link>
                </p>
              ) : latestPreview.replayFailed ? (
                <p className="nd1-applicant-meta">
                  We couldn&apos;t refresh results from your last search.{" "}
                  <Link to="/job-search">Try job search again</Link>
                </p>
              ) : latestPreview.jobs.length === 0 ? (
                <p className="nd1-applicant-meta">
                  We couldn&apos;t find any new roles for you. Try{" "}
                  <Link to="/job-search">searching here</Link>
                </p>
              ) : (
                <>
                  <ul className="nd1-applicant-job-list">
                    {latestPreview.jobs.slice(0, LATEST_JOBS_HOME_PREVIEW_MAX).map((job) => (
                      <li key={job.id}>
                        <ApplicantHomeJobPreviewCard job={job} />
                      </li>
                    ))}
                  </ul>
                  <div className="nd1-applicant-section-foot">
                    <button
                      type="button"
                      className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm nd1-applicant-section-foot__btn"
                      onClick={() => void goToJobSearchWithLastCriteria()}
                    >
                      See all jobs
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="nd1-applicant-split__column">
            <div className="nd1-applicant-section-card">
              <div className="nd1-applicant-section-head">
                <div className="nd1-applicant-section-head__title-wrap">
                  <span
                    className="nd1-applicant-section-head__icon-wrap nd1-applicant-section-head__icon-wrap--orange"
                    aria-hidden="true"
                  >
                    <Heart size={20} strokeWidth={1.75} fill="currentColor" />
                  </span>
                  <h2 className="nd1-applicant-section-head__title">Saved jobs</h2>
                </div>
                <button
                  type="button"
                  className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm"
                  onClick={() => navigate("/applicant/saved-jobs")}
                >
                  See all
                </button>
              </div>
              {savedJobsPreviewLoading ? (
                <p className="nd1-applicant-meta" aria-live="polite">
                  Loading…
                </p>
              ) : (savedJobsPreview?.total ?? 0) === 0 ? null : (
                <ul className="nd1-applicant-job-list">
                  {(savedJobsPreview?.jobs ?? []).map((job) => (
                    <li key={job.id}>
                      <ApplicantHomeJobPreviewCard job={job} />
                    </li>
                  ))}
                </ul>
              )}
              {!savedJobsPreviewLoading && (savedJobsPreview?.total ?? 0) > 0 ? (
                <div className="nd1-applicant-section-foot">
                  <button
                    type="button"
                    className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm nd1-applicant-section-foot__btn"
                    onClick={() => navigate("/applicant/saved-jobs")}
                  >
                    See all saved jobs
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="nd1-applicant-section" aria-labelledby="applicant-applied-jobs-title">
          <div className="nd1-applicant-section-card">
            <div className="nd1-applicant-section-head">
              <div className="nd1-applicant-section-head__title-wrap">
                <span className="nd1-applicant-section-head__icon-wrap" aria-hidden="true">
                  <AppliedStatusIcon size={20} />
                </span>
                <h2 id="applicant-applied-jobs-title" className="nd1-applicant-section-head__title">
                  Applied jobs
                </h2>
              </div>
              {!appliedJobsPreviewLoading && (appliedJobsPreview?.total ?? 0) > 0 ? (
                <button
                  type="button"
                  className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm"
                  onClick={() => navigate("/applicant/applied-jobs")}
                >
                  See all
                </button>
              ) : null}
            </div>
            {appliedJobsPreviewLoading ? (
              <p className="nd1-applicant-meta" aria-live="polite">
                Loading…
              </p>
            ) : (appliedJobsPreview?.total ?? 0) === 0 ? (
              <div className="nd1-applicant-empty-state">
                <div className="nd1-applicant-empty-state__icon-wrap" aria-hidden="true">
                  <AppliedStatusIcon className="nd1-applicant-empty-state__icon" size={36} />
                </div>
                <div className="nd1-applicant-empty-state__copy">
                  <p className="nd1-applicant-empty-state__title">You haven&apos;t applied to any jobs yet</p>
                  <p className="nd1-applicant-empty-state__text">
                    When you apply to jobs, they will appear here.
                  </p>
                  <button
                    type="button"
                    className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm"
                    onClick={() => navigate("/job-search")}
                  >
                    Browse jobs
                  </button>
                </div>
              </div>
            ) : (
              <>
                <ul className="nd1-applicant-job-list">
                  {(appliedJobsPreview?.jobs ?? []).slice(0, APPLIED_JOBS_HOME_PREVIEW_MAX).map((job) => (
                    <li key={job.id}>
                      <Link to={jobPublicHref(job)} className="nd1-applicant-job-card">
                        <span className="nd1-applicant-job-card__title">{job.title || "Role"}</span>
                        {job.company?.trim() ? (
                          <span className="nd1-applicant-job-card__company">{job.company.trim()}</span>
                        ) : null}
                        {job.appliedAt ? (
                          <span className="nd1-applicant-job-card__applied">
                            Applied on {formatAppliedOnFromIso(job.appliedAt)}
                          </span>
                        ) : null}
                        <LatestJobsPreviewPills job={job} />
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="nd1-applicant-section-foot">
                  <button
                    type="button"
                    className="nd1-btn nd1-btn--outline nd1-applicant-btn-sm"
                    onClick={() => navigate("/applicant/applied-jobs")}
                  >
                    See all
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
    </ApplicantShell>
  );
}

export default ApplicantHomePage;
